import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import {
    TrialGroupItemTemplateCategoryBreadcrumbs,
    TrialGroupItemTemplateCategoryCreateBreadcrumbs,
} from "src/helpers/breadcrumbHelper";
import { SettingsTrialGroupSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupItemTemplateCategoriesActions from "../../../actions/trialGroupItemTemplateCategories";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    NumberFormInput,
    TextFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialGroupItemTemplateCategoryPage.scss";

interface TrialGroupItemTemplateCategoryPageProps {
    form: Dtos.TrialGroupItemTemplateCategory & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialGroupItemTemplateCategory: Dtos.TrialGroupItemTemplateCategory;
    lookups: Dtos.Lookup[];
    loadingTrialGroupItemTemplateCategory: boolean;
    loadTrialGroupItemTemplateCategoryFailure: boolean;
    loadTrialGroupItemTemplateCategoryErrorCode: string;
    savingTrialGroupItemTemplateCategory: boolean;
    saveTrialGroupItemTemplateCategorySuccess: boolean;
    saveTrialGroupItemTemplateCategoryFailure: boolean;
    creatingTrialGroupItemTemplateCategory: boolean;
    createTrialGroupItemTemplateCategorySuccess: boolean;
    validatingTrialGroupItemTemplateCategory: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialGroupItemTemplateCategory: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryClearActionCreator;
    loadTrialGroupItemTemplateCategory: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryLoadByIdActionCreator;
    saveTrialGroupItemTemplateCategory: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategorySaveActionCreator;
    createTrialGroupItemTemplateCategory: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryCreateActionCreator;
    validateTrialGroupItemTemplateCategory: TrialGroupItemTemplateCategoriesActions.ITrialGroupItemTemplateCategoryValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupItemTemplateCategory";

const TrialGroupItemTemplateCategoryPage: FC<
    TrialGroupItemTemplateCategoryPageProps
> = ({
    form,
    formState,
    formProperties,
    trialGroupItemTemplateCategory,
    lookups,
    loadingTrialGroupItemTemplateCategory,
    loadTrialGroupItemTemplateCategoryFailure,
    loadTrialGroupItemTemplateCategoryErrorCode,
    savingTrialGroupItemTemplateCategory,
    saveTrialGroupItemTemplateCategorySuccess,
    saveTrialGroupItemTemplateCategoryFailure,
    creatingTrialGroupItemTemplateCategory,
    createTrialGroupItemTemplateCategorySuccess,
    validatingTrialGroupItemTemplateCategory,
    validationFailures,
    permissions,
    clearTrialGroupItemTemplateCategory,
    loadTrialGroupItemTemplateCategory,
    saveTrialGroupItemTemplateCategory,
    createTrialGroupItemTemplateCategory,
    validateTrialGroupItemTemplateCategory,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialGroupItemTemplateCategoryId } = useParams<{
        trialGroupItemTemplateCategoryId: string;
    }>();
    const trialGroupItemTemplateCategoryIdNumber = parseInt(
        trialGroupItemTemplateCategoryId,
        10
    );

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupItemTemplateCategory =
            {} as Dtos.TrialGroupItemTemplateCategory;

        loadForm(
            reduxFormName,
            Object.assign({ ...trialGroupItemTemplateCategory }, extraData)
        );
    };


    // Load trial group item template category
    useEffect(() => {
        if (
            trialGroupItemTemplateCategoryIdNumber &&
            !trialGroupItemTemplateCategory &&
            !loadingTrialGroupItemTemplateCategory
        ) {
            setDrawerItems(SettingsTrialGroupSubMenu(permissions));
            setBreadcrumbs(
                TrialGroupItemTemplateCategoryBreadcrumbs(
                    trialGroupItemTemplateCategoryIdNumber
                )
            );
            loadTrialGroupItemTemplateCategory(
                trialGroupItemTemplateCategoryIdNumber
            );
        } else if (
            !trialGroupItemTemplateCategoryIdNumber &&
            !creatingTrialGroupItemTemplateCategory &&
            !createTrialGroupItemTemplateCategorySuccess
        ) {
            createTrialGroupItemTemplateCategory();
            setBreadcrumbs(TrialGroupItemTemplateCategoryCreateBreadcrumbs());
            setDrawerItems([]);
        } else if (trialGroupItemTemplateCategory && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        trialGroupItemTemplateCategoryIdNumber,
        trialGroupItemTemplateCategory,
        setDrawerItems,
        SettingsTrialGroupSubMenu,
        permissions,
        setBreadcrumbs,
        TrialGroupItemTemplateCategoryBreadcrumbs,
        loadTrialGroupItemTemplateCategory,
        createTrialGroupItemTemplateCategory,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialGroupItemTemplateCategory,
        creatingTrialGroupItemTemplateCategory,
        createTrialGroupItemTemplateCategorySuccess,
        TrialGroupItemTemplateCategoryCreateBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupItemTemplateCategoryFailure) {
            navigate(`/error/${loadTrialGroupItemTemplateCategoryErrorCode}`);
        }
    }, [
        loadTrialGroupItemTemplateCategoryFailure,
        loadTrialGroupItemTemplateCategoryErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (
            trialGroupItemTemplateCategoryIdNumber &&
            trialGroupItemTemplateCategory?.value
        ) {
            setBreadcrumbs(
                TrialGroupItemTemplateCategoryBreadcrumbs(
                    trialGroupItemTemplateCategoryIdNumber,
                    trialGroupItemTemplateCategory?.value
                )
            );
        }
    }, [
        trialGroupItemTemplateCategoryIdNumber,
        trialGroupItemTemplateCategory,
        setBreadcrumbs,
        TrialGroupItemTemplateCategoryBreadcrumbs,
    ]);

    // Reload trial group item template category after saving
    useEffect(() => {
        if (
            !savingTrialGroupItemTemplateCategory &&
            saveTrialGroupItemTemplateCategorySuccess
        ) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group item template category saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = "/settings/trialgroup/itemtemplatecategories";
                navigate(url);
            } else if (!trialGroupItemTemplateCategoryIdNumber) {
                let url: string = `/settings/trialgroup/itemtemplatecategories/${trialGroupItemTemplateCategory?.id}`;
                navigate(url);
                clearTrialGroupItemTemplateCategory();
            }
        }
    }, [
        savingTrialGroupItemTemplateCategory,
        saveTrialGroupItemTemplateCategorySuccess,
        trialGroupItemTemplateCategoryIdNumber,
        navigate,
        clearTrialGroupItemTemplateCategory,
        saveAndReturn,
        trialGroupItemTemplateCategory,
    ]);

    // Failed saving
    useEffect(() => {
        if (
            !savingTrialGroupItemTemplateCategory &&
            saveTrialGroupItemTemplateCategoryFailure
        ) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingTrialGroupItemTemplateCategory,
        saveTrialGroupItemTemplateCategoryFailure,
    ]);

    // Cleanup effect to clear the trial group item template category on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupItemTemplateCategory();
            resetForm(reduxFormName);
        };
    }, [clearTrialGroupItemTemplateCategory, resetForm, reduxFormName]);

    const onCancel = () => {
        let url: string = "/settings/trialgroup/itemtemplatecategories";
        navigate(url);
    };

    const handleOnSubmitFailed = (
        _data: Dtos.TrialGroupItemTemplateCategory
    ) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupItemTemplateCategory =
                {} as Dtos.TrialGroupItemTemplateCategory;

            await saveTrialGroupItemTemplateCategory(
                Object.assign({ ...data }, changes)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupItemTemplateCategory =
                {} as Dtos.TrialGroupItemTemplateCategory;

            if (
                form &&
                hasBeenSubmitted &&
                !validatingTrialGroupItemTemplateCategory
            ) {
                await validateTrialGroupItemTemplateCategory(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialGroupItemTemplateCategoriesUpdate,
                Dtos.Permission.TrialGroupItemTemplateCategoriesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupItemTemplateCategory ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row pt-4 pb-2 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "trialGroupItemTemplateCategory",
                                        {
                                            value: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-2",
                                                fieldClassName:
                                                    "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                            <div className="form-row pt-2 pb-4 px-4">
                                <div className="col-md-6">
                                    {renderFormControls(
                                        form,
                                        "trialGroupItemTemplateCategory",
                                        {
                                            rank: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: NumberFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName:
                                                    "col-form-label col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-6 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "trialGroupItemTemplateCategory",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName:
                                                    "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-4",
                                                fieldClassName:
                                                    "col-sm-8 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroupItemTemplateCategory",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialGroupItemTemplateCategory,
        formState,
        handleOnSubmit,
        handleOnSubmitFailed,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
    ]);

    return (
        <TrialLookupsLayout
            loading={loadingTrialGroupItemTemplateCategory}
            permission={Dtos.Permission.TrialGroupItemTemplateCategoriesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupItemTemplateCategoryPageProps> => {
    let trialGroupItemTemplateCategory:
        | Dtos.TrialGroupItemTemplateCategory
        | undefined = !(
        state.trialGroupItemTemplateCategory.formData instanceof Array
    )
        ? state.trialGroupItemTemplateCategory.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialGroupItemTemplateCategory: trialGroupItemTemplateCategory!,
        loadingTrialGroupItemTemplateCategory:
            state.trialGroupItemTemplateCategory.loadState &&
            state.trialGroupItemTemplateCategory.loadState.status ==
                RequestState.Pending,
        loadTrialGroupItemTemplateCategoryFailure:
            state.trialGroupItemTemplateCategory.loadState &&
            state.trialGroupItemTemplateCategory.loadState.status ==
                RequestState.Failure,
        loadTrialGroupItemTemplateCategoryErrorCode:
            state.trialGroupItemTemplateCategory.loadState.errorCode,
        savingTrialGroupItemTemplateCategory:
            state.trialGroupItemTemplateCategory.saveState &&
            state.trialGroupItemTemplateCategory.saveState.status ==
                RequestState.Pending,
        saveTrialGroupItemTemplateCategorySuccess:
            state.trialGroupItemTemplateCategory.saveState &&
            state.trialGroupItemTemplateCategory.saveState.status ==
                RequestState.Success,
        saveTrialGroupItemTemplateCategoryFailure:
            state.trialGroupItemTemplateCategory.saveState &&
            state.trialGroupItemTemplateCategory.saveState.status ==
                RequestState.Failure,
        creatingTrialGroupItemTemplateCategory:
            state.trialGroupItemTemplateCategory.createState &&
            state.trialGroupItemTemplateCategory.createState.status ==
                RequestState.Pending,
        createTrialGroupItemTemplateCategorySuccess:
            state.trialGroupItemTemplateCategory.createState &&
            state.trialGroupItemTemplateCategory.createState.status ==
                RequestState.Success,
        validatingTrialGroupItemTemplateCategory:
            state.trialGroupItemTemplateCategory.validateState &&
            state.trialGroupItemTemplateCategory.validateState.status ==
                RequestState.Pending,
        permissions: permissions,
        lookups: [],
        form: state.reduxForms.trialGroupItemTemplateCategory,
        formState: state.trialGroupItemTemplateCategory.formState,
        formProperties: state.trialGroupItemTemplateCategory.formProperties,
        validationFailures: trialGroupItemTemplateCategory
            ? state.trialGroupItemTemplateCategory.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupItemTemplateCategoryPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupItemTemplateCategory: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.LoadTrialGroupItemTemplateCategoryById,
            dispatch
        ),
        clearTrialGroupItemTemplateCategory: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.Clear,
            dispatch
        ),
        saveTrialGroupItemTemplateCategory: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.SaveTrialGroupItemTemplateCategory,
            dispatch
        ),
        createTrialGroupItemTemplateCategory: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.CreateTrialGroupItemTemplateCategory,
            dispatch
        ),
        validateTrialGroupItemTemplateCategory: bindActionCreators(
            TrialGroupItemTemplateCategoriesActions.ValidateTrialGroupItemTemplateCategory,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupItemTemplateCategoryPage);
